import { ROOT_NODE } from "@craftjs/core";
import type { Page as RegistrationPage } from "src/types";

import type { Page as ContentHubPage } from "@/content-hub/page/domain";
import type { ButtonAction } from "@/content-hub/types";

import type {
  ContentHubEventSummary,
  ContentHubEventSummaryRaw,
  EventPromotionConfig,
  EventSummaryUnavailableReason,
  PrimaryEventConfig,
} from "./types";
import { EVENT_PROMOTION_BLOCK_PROP, PRIMARY_EVENT_PAGE_PROP } from "./types";

type Page = ContentHubPage | RegistrationPage;

type NodeWithProps = {
  data?: {
    props?: Record<string, unknown>;
  };
  props?: Record<string, unknown>;
};

const normalizeEventExternalId = (eventExternalId?: string | null) => {
  if (!eventExternalId) return null;

  const trimmedEventExternalId = eventExternalId.trim();

  return trimmedEventExternalId || null;
};

const getPropsFromNode = (node: unknown) => {
  const nodeWithProps = node as NodeWithProps;

  return nodeWithProps.data?.props ?? nodeWithProps.props;
};

export const getPrimaryEventExternalIdFromPage = (page: Page) => {
  const rootNode = page.content[ROOT_NODE];
  const primaryEventConfig = getPropsFromNode(rootNode)?.[
    PRIMARY_EVENT_PAGE_PROP
  ] as PrimaryEventConfig;

  return normalizeEventExternalId(primaryEventConfig?.eventExternalId);
};

const getEventPromotionConfigFromNode = (node: unknown) => {
  const props = getPropsFromNode(node);

  return props?.[EVENT_PROMOTION_BLOCK_PROP] as
    | EventPromotionConfig
    | undefined;
};

export const getEventExternalIdFromEventPromotion = (
  config: EventPromotionConfig,
  primaryEventExternalId: string | null
) => {
  if (config.source === "none") return null;

  if (config.source === "primary") {
    return primaryEventExternalId;
  }

  return normalizeEventExternalId(config.eventExternalId);
};

const getButtonActionFromNode = (node: unknown) => {
  const props = getPropsFromNode(node);

  return props?.action as ButtonAction | undefined;
};

const getEventExternalIdFromButtonAction = (
  action: ButtonAction | undefined,
  primaryEventExternalId: string | null
) => {
  if (!action) return null;

  if (action.kind === "LinkedEvent") return primaryEventExternalId;

  if (action.kind === "Event") {
    return normalizeEventExternalId(action.eventExternalId);
  }

  return null;
};

export const nodeUsesHubLinkedEventButtonAction = (node: unknown) => {
  return getButtonActionFromNode(node)?.kind === "LinkedEvent";
};

export const nodeUsesPrimaryEventPromotion = (node: unknown) => {
  const config = getEventPromotionConfigFromNode(node);

  return config?.source === "primary";
};

export const getHubLinkedEventDependencyCountFromNodes = (
  nodes: Record<string, unknown>
) =>
  Object.values(nodes).reduce<number>((count, node) => {
    const nodeDependencyCount = [
      nodeUsesHubLinkedEventButtonAction(node),
      nodeUsesPrimaryEventPromotion(node),
    ].filter(Boolean).length;

    return count + nodeDependencyCount;
  }, 0);

export const getUniqueEventExternalIdsFromPage = (page: Page) => {
  const primaryEventExternalId = getPrimaryEventExternalIdFromPage(page);

  const eventExternalIds = Object.values(page.content).reduce<string[]>(
    (accumulator, node) => {
      const eventPromotionConfig = getEventPromotionConfigFromNode(node);
      const buttonAction = getButtonActionFromNode(node);

      const eventExternalIds = [
        eventPromotionConfig
          ? getEventExternalIdFromEventPromotion(
              eventPromotionConfig,
              primaryEventExternalId
            )
          : null,
        getEventExternalIdFromButtonAction(
          buttonAction,
          primaryEventExternalId
        ),
      ];

      eventExternalIds.forEach((eventExternalId) => {
        if (eventExternalId) accumulator.push(eventExternalId);
      });

      return accumulator;
    },
    primaryEventExternalId ? [primaryEventExternalId] : []
  );

  return Array.from(new Set(eventExternalIds));
};

export const createUnavailableContentHubEventSummary = (
  eventExternalId: string,
  reason: EventSummaryUnavailableReason = "missing",
  title?: string | null
): ContentHubEventSummary => {
  const eventSummary: ContentHubEventSummary = {
    id: eventExternalId,
    status: "unavailable",
    reason,
  };

  if (title) {
    eventSummary.title = title;
  }

  return eventSummary;
};

export const mapContentHubEventSummaryRawToContentHubEventSummary = (
  event: ContentHubEventSummaryRaw
): ContentHubEventSummary => {
  const reason = event.deleted
    ? "deleted"
    : event.availability && event.availability !== "available"
    ? event.availability
    : null;

  if (reason) {
    return createUnavailableContentHubEventSummary(
      event.external_id,
      reason,
      event.title ?? event.name
    );
  }

  return {
    id: event.external_id,
    status: "available",
    slug: event.slug ?? null,
    title: event.title ?? event.name ?? "",
    description: event.description ?? null,
    image: event.image ?? event.image_url ?? null,
    logo: event.logo ?? event.logo_url ?? null,
    timeStart: event.time_start ?? null,
    timeEnd: event.time_end ?? null,
    timezone: event.timezone ?? null,
    registrationUrl: event.registration_url ?? null,
  };
};
